import { Contact } from "./Contact";
import SearchChat from "./SearchChat";
import PropTypes from 'prop-types';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import React, { useState, useEffect, useCallback } from "react";
import { 
    Avatar, 
    IconButton, 
    InputBase, 
    Paper, 
    Chip, 
    Checkbox, 
    Button, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions,
    FormControl,
    InputLabel,
    Select as MuiSelect,
    MenuItem,
    TextField,
    Menu,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import Select from 'react-select';
import SearchIcon from '@mui/icons-material/Search';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonIcon from '@mui/icons-material/Person';
import ClearIcon from '@mui/icons-material/Clear';
import DescriptionIcon from '@mui/icons-material/Description';
import axios from 'axios';
import { useAuth } from '../services/auth.service';

// Custom debounce hook
function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}

// Function to fix messages that should be marked as sent instead of unread
const fixMessageClassification = (contact) => {
    if (!contact || !contact.lastMessage) return contact;
    
    const lastMessage = contact.lastMessage;
    
    // Check if this is a template message that should be marked as sent
    if (lastMessage.entry && 
        lastMessage.entry[0] && 
        lastMessage.entry[0].changes && 
        lastMessage.entry[0].changes[0] && 
        lastMessage.entry[0].changes[0].value && 
        lastMessage.entry[0].changes[0].value.messages && 
        lastMessage.entry[0].changes[0].value.messages[0]) {
        
        const message = lastMessage.entry[0].changes[0].value.messages[0];
        
        // Check if it's a template message (has text.body.name)
        if (message.text && 
            message.text.body && 
            typeof message.text.body === 'object' && 
            message.text.body.name) {
            
            // This is a template message, it should be marked as sent
            console.log('Fixing template message classification for contact:', contact.NombreCompleto, 'template:', message.text.body.name);
            return {
                ...contact,
                sentMessage: true,
                newMessages: 0
            };
        }
        
        // Check if the message is from our number (sent message)
        if (message.from === process.env.REACT_APP_PHONE_NUMBER) {
            return {
                ...contact,
                sentMessage: true,
                newMessages: 0
            };
        }
        
        // Check for specific delivery issue cases
        // If the message has sentMessage: false but newMessages > 0 and it's a template
        // or if the message appears to be from our system but marked as unread
        if (contact.sentMessage === false && contact.newMessages > 0) {
            // Check if this looks like a sent message that got misclassified
            if (message.text && 
                message.text.body && 
                typeof message.text.body === 'object' && 
                message.text.body.name) {
                // Template message that should be marked as sent
                console.log('Fixing misclassified template message for contact:', contact.NombreCompleto, 'template:', message.text.body.name);
                return {
                    ...contact,
                    sentMessage: true,
                    newMessages: 0
                };
            }
            
            // Check if the message has a "sent_from_id" indicating it was sent by our system
            if (lastMessage.sent_from_id && lastMessage.sent_from_id !== "generated by system") {
                return {
                    ...contact,
                    sentMessage: true,
                    newMessages: 0
                };
            }
            
            // Check for the specific case where the message has "sent_from_id": "generated by system"
            // This indicates it was generated by our system but marked as unread
            if (lastMessage.sent_from_id === "generated by system") {
                console.log('Fixing message classification for contact:', contact.NombreCompleto, 'from unread to sent');
                return {
                    ...contact,
                    sentMessage: true,
                    newMessages: 0
                };
            }
        }
    }
    
    return contact;
};

function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            style={{ 
                height: '100%', 
                display: value === index ? 'flex' : 'none',
                flexDirection: 'column' 
            }}
            {...other}
        >
            <Box sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                {children}
            </Box>
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

// WhatsApp-style search component with tag selection and search filters
function WhatsAppSearch({ search, handleSearch, onTagSelect, availableTags, availableTemplates, selectionMode, toggleSelectionMode }) {
    const [showTagSelector, setShowTagSelector] = useState(false);
    const [selectedTags, setSelectedTags] = useState([]);
    const [showTemplateSelector, setShowTemplateSelector] = useState(false);
    const [selectedTemplates, setSelectedTemplates] = useState([]);
    const [showSearchFilters, setShowSearchFilters] = useState(false);
    const [activeFilter, setActiveFilter] = useState('');
    const searchInputRef = React.useRef(null);
    const [localSearch, setLocalSearch] = useState(search);
    
    // Debounce the search with 500ms delay
    const debouncedSearch = useDebounce(localSearch, 500);
    
    // Update local search when prop changes
    useEffect(() => {
        setLocalSearch(search);
    }, [search]);
    
    // Update parent search when debounced value changes
    useEffect(() => {
        if (debouncedSearch !== search) {
            handleSearch({ target: { value: debouncedSearch } });
        }
    }, [debouncedSearch, handleSearch, search]);

    const handleClearSearch = () => {
        setLocalSearch('');
        setSelectedTags([]);
        setActiveFilter('');
    };

    const handleRemoveChip = (chipToRemove) => {
        const newSearch = searchChips
            .filter(chip => chip !== chipToRemove)
            .map(chip => chip.fullText)
            .join(' ');
        handleSearch({ target: { value: newSearch } });
    };

    // Parse search input to extract search types and terms
    const parseSearchInput = (searchText) => {
        if (!searchText || searchText.trim() === '') return [];
        
        const searchParts = searchText.split(' ');
        const chips = [];
        
        searchParts.forEach(part => {
            if (part.includes(':')) {
                const [type, ...queryParts] = part.split(':');
                const query = queryParts.join(':');
                if (type && query) {
                    chips.push({ type, query, fullText: part });
                }
            } else if (part.trim()) {
                // Default to general search if no type specified
                chips.push({ type: 'general', query: part.trim(), fullText: part.trim() });
            }
        });
        
        return chips;
    };

    const searchChips = parseSearchInput(localSearch);

    // Get chip styling and labels based on search type
    const getChipConfig = (type) => {
        const configs = {
            tag: { label: 'Etiqueta', color: '#e8f5e8', textColor: '#008069', icon: '🏷️' },
            tel: { label: 'Teléfono', color: '#e3f2fd', textColor: '#1976d2', icon: '📞' },
            nombre: { label: 'Nombre', color: '#f3e5f5', textColor: '#7b1fa2', icon: '👤' },
            notas: { label: 'Notas', color: '#fff3e0', textColor: '#f57c00', icon: '📝' },
            ejecutivo: { label: 'Ejecutivo', color: '#e8f5e8', textColor: '#2e7d32', icon: '👨‍💼' },
            email: { label: 'Email', color: '#e0f2f1', textColor: '#00695c', icon: '📧' },
            sucursal: { label: 'Sucursal', color: '#fce4ec', textColor: '#c2185b', icon: '🏢' },
            template: { label: 'Template', color: '#fff8e1', textColor: '#f57f17', icon: '📋' },
            general: { label: 'General', color: '#f5f5f5', textColor: '#666666', icon: '🔍' }
        };
        return configs[type] || configs.general;
    };

    const searchFilters = [
        { key: 'tag', label: 'Etiquetas', icon: '🏷️' },
        { key: 'tel', label: 'Teléfono', icon: '📞' },
        { key: 'nombre', label: 'Nombre', icon: '👤' },
        { key: 'notas', label: 'Notas', icon: '📝' },
        { key: 'ejecutivo', label: 'Ejecutivo', icon: '👨‍💼' },
        { key: 'email', label: 'Email', icon: '📧' },
        { key: 'sucursal', label: 'Sucursal', icon: '🏢' },
        { key: 'template', label: 'Template', icon: '📋' }
    ];

    const handleTagClick = (tag) => {
        const newSelectedTags = selectedTags.includes(tag) 
            ? selectedTags.filter(t => t !== tag)
            : [...selectedTags, tag];
        
        setSelectedTags(newSelectedTags);
        
        // Add tag to search
        const tagSearch = newSelectedTags.length > 0 ? `tag:${newSelectedTags.join(',')}` : '';
        const currentSearch = localSearch.replace(/tag:[^,\s]+(?:,|$)/g, '').trim();
        const newSearch = currentSearch ? `${currentSearch} ${tagSearch}` : tagSearch;
        
        setLocalSearch(newSearch);
        
        // Hide tag selector menu
        setShowTagSelector(false);
        
        // Focus the search input after tag selection
        setTimeout(() => {
            if (searchInputRef.current) {
                searchInputRef.current.focus();
            } else {
                // Try alternative method
                const inputElement = document.querySelector('input[placeholder*="Buscar"]');
                if (inputElement) {
                    inputElement.focus();
                }
            }
        }, 100);
    };

    const handleTemplateClick = (template) => {
        const newSelectedTemplates = selectedTemplates.includes(template) 
            ? selectedTemplates.filter(t => t !== template)
            : [...selectedTemplates, template];
        
        setSelectedTemplates(newSelectedTemplates);
        
        // Add template to search
        const templateSearch = newSelectedTemplates.length > 0 ? `template:${newSelectedTemplates.join(',')}` : '';
        const currentSearch = localSearch.replace(/template:[^,\s]+(?:,|$)/g, '').trim();
        const newSearch = currentSearch ? `${currentSearch} ${templateSearch}` : templateSearch;
        
        setLocalSearch(newSearch);
        
        // Hide template selector menu
        setShowTemplateSelector(false);
        
        // Focus the search input after template selection
        setTimeout(() => {
            if (searchInputRef.current) {
                searchInputRef.current.focus();
            } else {
                // Try alternative method
                const inputElement = document.querySelector('input[placeholder*="Buscar"]');
                if (inputElement) {
                    inputElement.focus();
                }
            }
        }, 100);
    };

    const clearTagSearch = () => {
        setSelectedTags([]);
        const newSearch = search.replace(/tag:[^,\s]+(?:,|$)/g, '').trim();
        handleSearch({ target: { value: newSearch } });
    };

    const clearTemplateSearch = () => {
        setSelectedTemplates([]);
        const newSearch = search.replace(/template:[^,\s]+(?:,|$)/g, '').trim();
        handleSearch({ target: { value: newSearch } });
    };

    const handleFilterClick = (filterKey) => {
        if (activeFilter === filterKey) {
            setActiveFilter('');
            // Remove filter prefix from search
            const newSearch = localSearch.replace(new RegExp(`${filterKey}:[^\\s]+\\s*`, 'g'), '').trim();
            setLocalSearch(newSearch);
        } else {
            setActiveFilter(filterKey);
            // Add filter prefix to search
            const currentSearch = localSearch.replace(/[a-z]+:[^\s]+\s*/g, '').trim();
            const newSearch = currentSearch ? `${filterKey}:${currentSearch}` : `${filterKey}:`;
            setLocalSearch(newSearch);
        }
        
        // Focus the search input after filter click
        setTimeout(() => {
            if (searchInputRef.current) {
                console.log('Focusing search input...');
                searchInputRef.current.focus();
            } else {
                console.log('Search input ref is null');
                // Try alternative method
                const inputElement = document.querySelector('input[placeholder*="Buscar"]');
                if (inputElement) {
                    console.log('Found input element, focusing...');
                    inputElement.focus();
                } else {
                    console.log('No input element found');
                }
            }
        }, 100);
    };

    return (
        <Box sx={{ 
            p: 1.5, 
            backgroundColor: '#f0f2f5',
            borderBottom: '1px solid #e9edef',
            flexShrink: 0,
            position: 'relative'
        }}>
            {/* Search Input */}
            <Paper
                sx={{
                    p: '2px 4px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    width: '100%',
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    boxShadow: 'none',
                    border: '1px solid #e9edef',
                    mb: 1,
                    minHeight: '48px'
                }}
            >
                <IconButton sx={{ p: '10px', mt: 0.5 }} aria-label="search">
                    <SearchIcon />
                </IconButton>
                <Box sx={{ 
                    flex: 1, 
                    display: 'flex', 
                    flexDirection: 'column',
                    minHeight: '40px',
                    py: 0.5
                }}>
                    {/* Search Chips */}
                    {searchChips.length > 0 && (
                        <Box sx={{ 
                            display: 'flex', 
                            flexWrap: 'wrap', 
                            gap: 0.5, 
                            mb: 0.5,
                            minHeight: '24px'
                        }}>
                            {searchChips.map((chip, index) => {
                                const config = getChipConfig(chip.type);
                                return (
                                    <Chip
                                        key={index}
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <span style={{ fontSize: '10px' }}>{config.icon}</span>
                                                <span style={{ fontSize: '10px' }}>{config.label}</span>
                                                <span style={{ fontSize: '10px', fontWeight: 500 }}>:</span>
                                                <span style={{ fontSize: '10px', fontWeight: 500 }}>{chip.query}</span>
                                            </Box>
                                        }
                                        onDelete={() => handleRemoveChip(chip)}
                                        sx={{
                                            backgroundColor: config.color,
                                            color: config.textColor,
                                            fontSize: '10px',
                                            height: '20px',
                                            '& .MuiChip-deleteIcon': {
                                                fontSize: '12px',
                                                color: config.textColor
                                            },
                                            '&:hover': {
                                                backgroundColor: config.color,
                                                opacity: 0.8
                                            }
                                        }}
                                        size="small"
                                    />
                                );
                            })}
                        </Box>
                    )}
                    {/* Search Input */}
                    <InputBase
                        inputRef={searchInputRef}
                        sx={{ 
                            flex: 1,
                            '& .MuiInputBase-input': {
                                '&:focus': {
                                    outline: 'none',
                                    border: 'none',
                                    boxShadow: 'none'
                                }
                            },
                            '&.Mui-focused': {
                                '& .MuiOutlinedInput-notchedOutline': {
                                    border: 'none'
                                }
                            }
                        }}
                        placeholder={activeFilter ? `Buscar en ${activeFilter}...` : "Buscar por nombre, telefono o etiquetas"}
                        value={localSearch}
                        onChange={(e) => setLocalSearch(e.target.value)}
                    />
                </Box>
                {localSearch && localSearch.trim() !== '' && (
                    <IconButton 
                        sx={{ p: '8px', color: '#667781', mt: 0.5 }} 
                        onClick={handleClearSearch}
                        aria-label="clear search"
                    >
                        <ClearIcon fontSize="small" />
                    </IconButton>
                )}
                <IconButton 
                    sx={{ p: '8px', color: '#008069', mt: 0.5 }} 
                    onClick={() => setShowSearchFilters(!showSearchFilters)}
                    aria-label="search filters"
                >
                    <MoreVertIcon fontSize="small" />
                </IconButton>
                <IconButton 
                    sx={{ p: '8px', color: '#008069', mt: 0.5 }} 
                    onClick={() => setShowTagSelector(!showTagSelector)}
                    aria-label="select tags"
                >
                    <LocalOfferIcon fontSize="small" />
                </IconButton>
                <IconButton 
                    sx={{ p: '8px', color: '#008069', mt: 0.5 }} 
                    onClick={() => setShowTemplateSelector(!showTemplateSelector)}
                    aria-label="select templates"
                >
                    <DescriptionIcon fontSize="small" />
                </IconButton>
                <IconButton 
                    sx={{ 
                        p: '8px', 
                        color: selectionMode ? '#008069' : '#667781', 
                        mt: 0.5,
                        backgroundColor: selectionMode ? 'rgba(0, 128, 105, 0.1)' : 'transparent',
                        '&:hover': {
                            backgroundColor: selectionMode ? 'rgba(0, 128, 105, 0.2)' : 'rgba(0, 0, 0, 0.04)'
                        }
                    }} 
                    onClick={toggleSelectionMode}
                    aria-label="toggle selection mode"
                >
                    <MoreVertIcon fontSize="small" style={{ transform: 'rotate(90deg)' }} />
                </IconButton>
            </Paper>

            {/* Search Filters */}
            {showSearchFilters && (
                <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 0.5, 
                    mb: 1,
                    p: 1,
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    border: '1px solid #e9edef'
                }}>
                    {searchFilters.map((filter) => (
                        <Box
                            key={filter.key}
                            onClick={() => handleFilterClick(filter.key)}
                            sx={{
                                backgroundColor: activeFilter === filter.key ? '#008069' : '#f0f2f5',
                                color: activeFilter === filter.key ? '#ffffff' : '#667781',
                                fontSize: '11px',
                                fontWeight: 500,
                                px: 1,
                                py: 0.5,
                                borderRadius: '12px',
                                border: '1px solid',
                                borderColor: activeFilter === filter.key ? '#008069' : '#e9edef',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                '&:hover': {
                                    backgroundColor: activeFilter === filter.key ? '#006d5a' : '#e4e6e8'
                                }
                            }}
                        >
                            <span style={{ fontSize: '10px' }}>{filter.icon}</span>
                            {filter.label}
                        </Box>
                    ))}
                </Box>
            )}

            {/* Selected Tags Display */}
            {selectedTags.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    {selectedTags.map((tag, index) => (
                        <Box
                            key={index}
                            sx={{
                                backgroundColor: '#e8f5e8',
                                color: '#008069',
                                fontSize: '11px',
                                fontWeight: 500,
                                px: 1,
                                py: 0.5,
                                borderRadius: '12px',
                                border: '1px solid #d4edda',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                cursor: 'pointer',
                                '&:hover': {
                                    backgroundColor: '#d1e7dd',
                                    borderColor: '#badbcc'
                                }
                            }}
                            onClick={() => handleTagClick(tag)}
                        >
                            <LocalOfferIcon sx={{ fontSize: '10px' }} />
                            {tag}
                            <span style={{ fontSize: '10px', marginLeft: '2px' }}>×</span>
                        </Box>
                    ))}
                    <Box
                        sx={{
                            color: '#dc3545',
                            fontSize: '11px',
                            cursor: 'pointer',
                            px: 1,
                            py: 0.5,
                            borderRadius: '8px',
                            '&:hover': {
                                backgroundColor: '#f8d7da'
                            }
                        }}
                        onClick={clearTagSearch}
                    >
                        Limpiar
                    </Box>
                </Box>
            )}

            {/* Selected Templates Display */}
            {selectedTemplates.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    {selectedTemplates.map((template, index) => (
                        <Box
                            key={index}
                            sx={{
                                backgroundColor: '#fff8e1',
                                color: '#f57f17',
                                fontSize: '11px',
                                fontWeight: 500,
                                px: 1,
                                py: 0.5,
                                borderRadius: '12px',
                                border: '1px solid #ffe082',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                cursor: 'pointer',
                                '&:hover': {
                                    backgroundColor: '#fff3cd',
                                    borderColor: '#ffd54f'
                                }
                            }}
                            onClick={() => handleTemplateClick(template)}
                        >
                            <DescriptionIcon sx={{ fontSize: '10px' }} />
                            {template}
                            <span style={{ fontSize: '10px', marginLeft: '2px' }}>×</span>
                        </Box>
                    ))}
                    <Box
                        sx={{
                            color: '#dc3545',
                            fontSize: '11px',
                            cursor: 'pointer',
                            px: 1,
                            py: 0.5,
                            borderRadius: '8px',
                            '&:hover': {
                                backgroundColor: '#f8d7da'
                            }
                        }}
                        onClick={clearTemplateSearch}
                    >
                        Limpiar
                    </Box>
                </Box>
            )}

            {/* Tag Selector Dropdown */}
            {showTagSelector && (
                <Paper
                    sx={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        backgroundColor: '#ffffff',
                        border: '1px solid #e9edef',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        mt: 0.5
                    }}
                >
                    <Box sx={{ p: 1 }}>
                        <Typography variant="caption" sx={{ color: '#667781', fontWeight: 500, mb: 1, display: 'block' }}>
                            Seleccionar etiquetas:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {availableTags.map((tag, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        backgroundColor: selectedTags.includes(tag.label) ? '#008069' : '#f0f2f5',
                                        color: selectedTags.includes(tag.label) ? '#ffffff' : '#667781',
                                        fontSize: '11px',
                                        fontWeight: 500,
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: '12px',
                                        border: '1px solid',
                                        borderColor: selectedTags.includes(tag.label) ? '#008069' : '#e9edef',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: selectedTags.includes(tag.label) ? '#006d5a' : '#e4e6e8'
                                        }
                                    }}
                                    onClick={() => handleTagClick(tag.label)}
                                >
                                    {tag.label}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Paper>
            )}

            {/* Template Selector Dropdown */}
            {showTemplateSelector && (
                <Paper
                    sx={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        backgroundColor: '#ffffff',
                        border: '1px solid #e9edef',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        mt: 0.5
                    }}
                >
                    <Box sx={{ p: 1 }}>
                        <Typography variant="caption" sx={{ color: '#667781', fontWeight: 500, mb: 1, display: 'block' }}>
                            Seleccionar templates:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {availableTemplates.map((template, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        backgroundColor: selectedTemplates.includes(template) ? '#f57f17' : '#f0f2f5',
                                        color: selectedTemplates.includes(template) ? '#ffffff' : '#667781',
                                        fontSize: '11px',
                                        fontWeight: 500,
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: '12px',
                                        border: '1px solid',
                                        borderColor: selectedTemplates.includes(template) ? '#f57f17' : '#e9edef',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: selectedTemplates.includes(template) ? '#e65100' : '#e4e6e8'
                                        }
                                    }}
                                    onClick={() => handleTemplateClick(template)}
                                >
                                    {template}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Paper>
            )}
        </Box>
    );
}

// Reusable panel for each contact list
function ContactListPanel({ 
    contacts, 
    loadContactMessages, 
    setLoading, 
    loadMoreContacts, 
    loadMoreSearchResults, 
    tabType, 
    isSearchResults, 
    searchPagination,
    selectionMode,
    selectedContacts,
    toggleContactSelection
}) {
    if (!contacts || contacts.length === 0) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '200px',
                color: '#667781'
            }}>
                <Typography variant="body2">No conversations available in {tabType}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ 
            overflowY: 'auto', 
            flex: 1,
            backgroundColor: '#ffffff'
        }}>
            {contacts.map((element, index) => {
                if (element.loadMore) {
                    return (
                        <Box
                            key={`loadMore-${index}`}
                            onClick={e => isSearchResults ? loadMoreSearchResults(e) : loadMoreContacts(e, tabType)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                p: 2,
                                cursor: 'pointer',
                                '&:hover': {
                                    backgroundColor: '#f5f6f6'
                                }
                            }}
                        >
                            <Typography 
                                variant="body2" 
                                sx={{ 
                                    textAlign: 'center', 
                                    width: '100%',
                                    color: '#008069',
                                    fontWeight: 500
                                }}
                            >
                                Load more
                            </Typography>
                        </Box>
                    );
                }

                // Apply message classification fix
                const fixedContact = fixMessageClassification(element);
                const contact = fixedContact;
                const lastMessage = fixedContact.lastMessage || { text: { body: "" }, timestamp: "" };

                return (
                    <Box
                        key={contact._id || index}
                        onClick={() => {
                            if (selectionMode) {
                                toggleContactSelection(contact);
                            } else {
                                loadContactMessages(index, tabType);
                                element.selected = true;
                                setLoading(true);
                            }
                        }}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f0f2f5',
                            backgroundColor: selectedContacts.some(c => c._id === contact._id) ? '#f0f2f5' : 'transparent',
                            '&:hover': {
                                backgroundColor: '#f5f6f6'
                            }
                        }}
                    >
                        {selectionMode && (
                            <Box sx={{ pl: 2 }}>
                                <Checkbox 
                                    checked={selectedContacts.some(c => c._id === contact._id)}
                                    sx={{ 
                                        color: '#667781',
                                        '&.Mui-checked': {
                                            color: '#008069',
                                        },
                                    }}
                                />
                            </Box>
                        )}
                        <Contact
                            name={contact.NombreCompleto || contact.name || ""}
                            draft={contact.draft ?? ""}
                            lastMessage={lastMessage}
                            lastMessageTimestamp={lastMessage.timestamp || ""}
                            newMessages={contact.newMessages}
                            etiquetas={contact.etiquetas || []}
                        />
                    </Box>
                );
            })}
            
            {/* Show Load More button for search results if there are more pages */}
            {isSearchResults && searchPagination && searchPagination.hasMore && (
                <Box
                    onClick={e => loadMoreSearchResults(e)}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 2,
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: '#f5f6f6'
                        }
                    }}
                >
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            textAlign: 'center', 
                            width: '100%',
                            color: '#008069',
                            fontWeight: 500
                        }}
                    >
                        Load more
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

ContactListPanel.propTypes = {
    contacts: PropTypes.array.isRequired,
    loadContactMessages: PropTypes.func.isRequired,
    setLoading: PropTypes.func.isRequired,
    loadMoreContacts: PropTypes.func.isRequired,
    loadMoreSearchResults: PropTypes.func,
    tabType: PropTypes.string,
    isSearchResults: PropTypes.bool,
    searchPagination: PropTypes.object,
};

export default function Contacts(props) {
    const {
        contacts,
        unreadContacts,
        sentContacts,
        automatedContacts,
        loadContactMessages,
        search,
        handleSearch,
        setLoading,
        loadMoreContacts,
        loadMoreSearchResults,
        searchPagination
    } = props;

    const [value, setValue] = useState(0);
    const [availableTags, setAvailableTags] = useState([]);
    const [availableTemplates, setAvailableTemplates] = useState([]);
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedContacts, setSelectedContacts] = useState([]);
    const [showActionsDialog, setShowActionsDialog] = useState(false);
    const [actionType, setActionType] = useState(null); // 'ejecutivo', 'etiquetas', 'notas'
    const [ejecutivos, setEjecutivos] = useState([]);
    const [bulkNotes, setBulkNotes] = useState("");
    const [bulkTags, setBulkTags] = useState([]);
    const [bulkEjecutivo, setBulkEjecutivo] = useState(null);
    const { user } = useAuth();

    const toggleSelectionMode = () => {
        setSelectionMode(!selectionMode);
        if (selectionMode) {
            setSelectedContacts([]);
        }
    };

    const toggleContactSelection = (contact) => {
        const isSelected = selectedContacts.some(c => c._id === contact._id);
        if (isSelected) {
            setSelectedContacts(selectedContacts.filter(c => c._id !== contact._id));
        } else {
            setSelectedContacts([...selectedContacts, contact]);
        }
    };

    const handleSelectAll = () => {
        let currentList = [];
        if (value === 0) currentList = fixedContacts;
        else if (value === 1) currentList = fixedSentContacts;
        else if (value === 2) currentList = fixedUnreadContacts;
        else if (value === 3) currentList = fixedAutomatedContacts;

        // Filter out "load more" items
        const actualContacts = currentList.filter(c => !c.loadMore);

        if (selectedContacts.length === actualContacts.length) {
            setSelectedContacts([]);
        } else {
            setSelectedContacts(actualContacts);
        }
    };

    const handleBulkAction = async () => {
        // Implementation for bulk actions
        console.log("Performing bulk action:", actionType, "on", selectedContacts.length, "contacts");
        
        try {
            setLoading(true);
            const contactIds = selectedContacts.map(c => c.Celular);
            
            let data = {};
            if (actionType === 'notas') data = { notas: bulkNotes };
            if (actionType === 'etiquetas') data = { etiquetas: bulkTags };
            if (actionType === 'ejecutivo') data = { ejecutivoAsignado: bulkEjecutivo };

            // We need an endpoint for bulk update or call multiple times
            // For now, let's assume we call the update endpoint for each contact
            // ideally there should be a bulk endpoint
            
            await Promise.all(contactIds.map(async (celular) => {
                const url = `${process.env.REACT_APP_BACKEND_API}/contacts/${celular}`;
                const config = {
                    headers: {
                        'Content-Type': 'application/json',
                        "x-access-token": user.accessToken,
                    },
                };
                return axios.put(url, data, config);
            }));

            console.log("Bulk action completed");
            setShowActionsDialog(false);
            setSelectionMode(false);
            setSelectedContacts([]);
            // Refresh contacts would be good here, but they are managed by props
            // Maybe alert user or trigger a refresh if possible
        } catch (error) {
            console.error("Error performing bulk action:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (event, newValue) => setValue(newValue);

    // Apply message classification fixes to all contact lists
    const fixedContacts = Array.isArray(contacts) ? contacts.map(fixMessageClassification) : [];
    const fixedUnreadContacts = Array.isArray(unreadContacts) ? unreadContacts.map(fixMessageClassification) : [];
    const fixedSentContacts = Array.isArray(sentContacts) ? sentContacts.map(fixMessageClassification) : [];
    const fixedAutomatedContacts = Array.isArray(automatedContacts) ? automatedContacts.map(fixMessageClassification) : [];

    // Fetch tags from API
    useEffect(() => {
        async function fetchTags() {
            try {
                const url = `${process.env.REACT_APP_BACKEND_API}/tag`;
                const config = {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        "x-access-token": user.accessToken,
                    },
                };

                const response = await axios.get(url, config);
                const tags = response.data.map((tag) => ({
                    value: tag._id,
                    label: tag.name
                }));
                setAvailableTags(tags.sort((a, b) => a.label.localeCompare(b.label)));
            } catch (error) {
                console.log('Error fetching tags:', error);
                setAvailableTags([]);
            }
        }
        
        async function fetchTemplates() {
            try {
                const url = `${process.env.REACT_APP_BACKEND_API}/template`;
                const config = {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        "x-access-token": user.accessToken,
                    },
                };

                const response = await axios.get(url, config);
                const templates = response.data.map((template) => template.name);
                setAvailableTemplates(templates.sort());
            } catch (error) {
                console.log('Error fetching templates:', error);
                setAvailableTemplates([]);
            }
        }
        
        async function fetchEjecutivos() {
            try {
                const url = `${process.env.REACT_APP_BACKEND_API}/ejecutivo`;
                const config = {
                    headers: {
                        'x-access-token': user.accessToken,
                        'Content-Type': 'application/json',
                    }
                };
                const response = await axios.get(url, config);
                const fetchedEjecutivos = response.data.map((ejecutivo) => ({
                    value: ejecutivo._id,
                    label: ejecutivo.firstName + " " + ejecutivo.lastName
                }));
                setEjecutivos(fetchedEjecutivos);
            } catch (error) {
                console.log('Error fetching ejecutivos:', error);
            }
        }
        
        fetchTags();
        fetchTemplates();
        fetchEjecutivos();
    }, [user.accessToken]);

    return (
        <Box 
            id="contactsContainer"
            sx={{ 
                width: '35%', 
                height: '100%',
                backgroundColor: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                borderRight: '1px solid #e9edef',
                overflow: 'hidden'
            }}
        >
            {/* Search Bar */}
            <WhatsAppSearch 
                search={search} 
                handleSearch={handleSearch} 
                availableTags={availableTags}
                availableTemplates={availableTemplates}
                selectionMode={selectionMode}
                toggleSelectionMode={toggleSelectionMode}
            />

            {/* Selection Toolbar */}
            {selectionMode && (
                <Box sx={{ 
                    p: 1, 
                    backgroundColor: '#f0f2f5', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    borderBottom: '1px solid #e9edef'
                }}>
                    <Button 
                        size="small" 
                        variant="outlined" 
                        onClick={handleSelectAll}
                        sx={{ 
                            color: '#008069', 
                            borderColor: '#008069',
                            textTransform: 'none',
                            '&:hover': {
                                borderColor: '#006d5a',
                                backgroundColor: 'rgba(0, 128, 105, 0.04)'
                            }
                        }}
                    >
                        {selectedContacts.length > 0 && selectedContacts.length === (value === 0 ? fixedContacts : value === 1 ? fixedSentContacts : value === 2 ? fixedUnreadContacts : fixedAutomatedContacts).filter(c => !c.loadMore).length 
                            ? 'Desmarcar Todos' 
                            : 'Seleccionar Todos'}
                    </Button>
                    
                    <Typography variant="body2" sx={{ flex: 1, color: '#667781', fontWeight: 500, ml: 1 }}>
                        {selectedContacts.length} seleccionados
                    </Typography>

                    <Button 
                        size="small" 
                        variant="contained" 
                        disabled={selectedContacts.length === 0}
                        onClick={() => setShowActionsDialog(true)}
                        sx={{ 
                            backgroundColor: '#008069',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: '#006d5a'
                            },
                            '&.Mui-disabled': {
                                backgroundColor: '#e9edef',
                                color: '#aebac1'
                            }
                        }}
                    >
                        Acciones
                    </Button>
                </Box>
            )}

            {/* Tabs */}
            <Box sx={{ 
                borderBottom: 1, 
                borderColor: '#e9edef',
                backgroundColor: '#ffffff',
                flexShrink: 0
            }}>
                <Tabs 
                    value={value} 
                    onChange={handleChange} 
                    aria-label="Contact list tabs"
                    sx={{
                        '& .MuiTab-root': {
                            color: '#667781',
                            fontWeight: 500,
                            textTransform: 'none',
                            fontSize: '14px',
                            minHeight: '40px',
                            padding: '8px 16px'
                        },
                        '& .Mui-selected': {
                            color: '#008069'
                        },
                        '& .MuiTabs-indicator': {
                            backgroundColor: '#008069'
                        }
                    }}
                >
                    <Tab label="Todos" {...a11yProps(0)} />
                    <Tab label="Enviados" {...a11yProps(1)} />
                    <Tab label="No Leidos" {...a11yProps(2)} />
                    <Tab label="Automatizaciones" {...a11yProps(3)} />
                </Tabs>
            </Box>

            {/* Tab Panels */}
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                <div style={{ 
                    display: value === 0 ? 'flex' : 'none',
                    height: '100%',
                    flexDirection: 'column'
                }}>
                    <ContactListPanel
                        contacts={fixedContacts}
                        loadContactMessages={loadContactMessages}
                        setLoading={setLoading}
                        loadMoreContacts={loadMoreContacts}
                        loadMoreSearchResults={loadMoreSearchResults}
                        tabType="inbox"
                        isSearchResults={search && search.trim() !== ''}
                        searchPagination={searchPagination}
                        selectionMode={selectionMode}
                        selectedContacts={selectedContacts}
                        toggleContactSelection={toggleContactSelection}
                    />
                </div>
                <div style={{ 
                    display: value === 1 ? 'flex' : 'none',
                    height: '100%',
                    flexDirection: 'column'
                }}>
                    <ContactListPanel
                        contacts={fixedSentContacts}
                        loadContactMessages={loadContactMessages}
                        setLoading={setLoading}
                        loadMoreContacts={loadMoreContacts}
                        loadMoreSearchResults={loadMoreSearchResults}
                        tabType="sent"
                        isSearchResults={search && search.trim() !== ''}
                        searchPagination={searchPagination}
                        selectionMode={selectionMode}
                        selectedContacts={selectedContacts}
                        toggleContactSelection={toggleContactSelection}
                    />
                </div>
                <div style={{ 
                    display: value === 2 ? 'flex' : 'none',
                    height: '100%',
                    flexDirection: 'column'
                }}>
                    <ContactListPanel
                        contacts={fixedUnreadContacts}
                        loadContactMessages={loadContactMessages}
                        setLoading={setLoading}
                        loadMoreContacts={loadMoreContacts}
                        loadMoreSearchResults={loadMoreSearchResults}
                        tabType="unread"
                        isSearchResults={search && search.trim() !== ''}
                        searchPagination={searchPagination}
                        selectionMode={selectionMode}
                        selectedContacts={selectedContacts}
                        toggleContactSelection={toggleContactSelection}
                    />
                </div>
                <div style={{ 
                    display: value === 3 ? 'flex' : 'none',
                    height: '100%',
                    flexDirection: 'column'
                }}>
                    <ContactListPanel
                        contacts={fixedAutomatedContacts}
                        loadContactMessages={loadContactMessages}
                        setLoading={setLoading}
                        loadMoreContacts={loadMoreContacts}
                        loadMoreSearchResults={loadMoreSearchResults}
                        tabType="automated"
                        isSearchResults={search && search.trim() !== ''}
                        searchPagination={searchPagination}
                        selectionMode={selectionMode}
                        selectedContacts={selectedContacts}
                        toggleContactSelection={toggleContactSelection}
                    />
                </div>
            </Box>

            {/* Actions Dialog */}
            <Dialog 
                open={showActionsDialog} 
                onClose={() => setShowActionsDialog(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: '12px' }
                }}
            >
                <DialogTitle sx={{ fontWeight: 600, color: '#111b21' }}>
                    Acciones para {selectedContacts.length} contactos
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <FormControl fullWidth>
                            <InputLabel id="action-type-label">¿Qué deseas hacer?</InputLabel>
                            <MuiSelect
                                labelId="action-type-label"
                                value={actionType || ""}
                                label="¿Qué deseas hacer?"
                                onChange={(e) => setActionType(e.target.value)}
                            >
                                <MenuItem value="ejecutivo">Asignar Ejecutivo</MenuItem>
                                <MenuItem value="etiquetas">Asignar Etiquetas</MenuItem>
                                <MenuItem value="notas">Agregar Nota</MenuItem>
                            </MuiSelect>
                        </FormControl>

                        {actionType === 'ejecutivo' && (
                            <Box>
                                <Typography variant="body2" sx={{ mb: 1, color: '#667781', fontWeight: 500 }}>
                                    Seleccionar Ejecutivo:
                                </Typography>
                                <Select
                                    isMulti
                                    options={ejecutivos}
                                    onChange={(options) => setBulkEjecutivo(options ? options.map(o => o.value) : [])}
                                    placeholder="Buscar ejecutivos..."
                                    isClearable
                                />
                            </Box>
                        )}

                        {actionType === 'etiquetas' && (
                            <Box>
                                <Typography variant="body2" sx={{ mb: 1, color: '#667781', fontWeight: 500 }}>
                                    Seleccionar Etiquetas:
                                </Typography>
                                <Select
                                    isMulti
                                    options={availableTags}
                                    onChange={(options) => setBulkTags(options ? options.map(o => ({ _id: o.value, name: o.label, label: o.label })) : [])}
                                    placeholder="Buscar etiquetas..."
                                />
                            </Box>
                        )}

                        {actionType === 'notas' && (
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Nota para los contactos"
                                placeholder="Escribe la nota que se aplicará a todos los seleccionados..."
                                value={bulkNotes}
                                onChange={(e) => setBulkNotes(e.target.value)}
                            />
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button 
                        onClick={() => setShowActionsDialog(false)} 
                        sx={{ color: '#667781', textTransform: 'none' }}
                    >
                        Cancelar
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={handleBulkAction}
                        disabled={!actionType || (actionType === 'ejecutivo' && !bulkEjecutivo) || (actionType === 'etiquetas' && bulkTags.length === 0) || (actionType === 'notas' && !bulkNotes)}
                        sx={{ 
                            backgroundColor: '#008069', 
                            textTransform: 'none',
                            '&:hover': { backgroundColor: '#006d5a' }
                        }}
                    >
                        Aplicar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

Contacts.propTypes = {
    contacts: PropTypes.array.isRequired,
    unreadContacts: PropTypes.array.isRequired,
    sentContacts: PropTypes.array.isRequired,
    automatedContacts: PropTypes.array,
    loadContactMessages: PropTypes.func.isRequired,
    search: PropTypes.string,
    handleSearch: PropTypes.func.isRequired,
    setLoading: PropTypes.func.isRequired,
    loadMoreContacts: PropTypes.func.isRequired,
    loadMoreSearchResults: PropTypes.func,
    searchPagination: PropTypes.object,
};